import { Component, ErrorInfo, ReactNode } from 'react';
import { ErrorBoundaryState } from '../../types';
import Button from '../ui/Button';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className='min-h-screen bg-gray-50 flex items-center justify-center px-4'>
          <div className='max-w-md w-full text-center'>
            <div className='mb-8'>
              <div className='mx-auto h-24 w-24 bg-red-100 rounded-full flex items-center justify-center mb-4'>
                <svg
                  className='h-12 w-12 text-red-600'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
                  />
                </svg>
              </div>
              <h1 className='text-2xl font-bold text-gray-900 mb-2'>¡Oops! Algo salió mal</h1>
              <p className='text-gray-600 mb-6'>
                Ha ocurrido un error inesperado. Por favor, intenta recargar la página.
              </p>
            </div>

            <div className='space-y-4'>
              <Button variant='primary' onClick={this.handleReset} className='w-full'>
                Intentar de nuevo
              </Button>

              <Button
                variant='secondary'
                onClick={() => window.location.reload()}
                className='w-full'
              >
                Recargar página
              </Button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className='mt-8 text-left'>
                <summary className='cursor-pointer text-sm text-gray-500 hover:text-gray-700'>
                  Detalles del error (desarrollo)
                </summary>
                <pre className='mt-2 text-xs text-red-600 bg-red-50 p-4 rounded-lg overflow-auto'>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
